/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Utility functions for similar listings
 */

import { getAPIFriendlyFilters } from '@zcode-ui/filters'
import type { BuildingListingCollection } from '@zcode/bizos'
import { PropertyFeatures, PropertyType } from '@zcode/enums'
import { localStorageSingleton, oforEach } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import { MAX_FILTER_PRICE } from '@zcode/nr/es/shared/constants/filter'
import { feetToDegreesAtLatitude } from '@zcode/nr/es/shared/utils'
import type {
  Filters,
  FloorplanListing,
  Listable,
  ListableBuilding,
  ListableListing,
  NormalFloorplanListingData,
  Property,
} from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import { isNumber } from '@zcode/utils'
import { logger } from '../logger'

export const THREE_MILE_DIAG_BOX_SIDE_FEET = 11200
export const SIX_MILE_DIAG_BOX_SIDE_FEET = 22400
// 11200 / 3 * 7 = 26133.33
export const SEVEN_MILE_DIAG_BOX_SIDE_FEET = 26133.33

const selectFloorplan = (
  filters: Filters,
  floorplans: ReadonlyArray<FloorplanListing<NormalFloorplanListingData>> | undefined
) => {
  // early exit
  if (!floorplans || !Array.isArray(floorplans) || !floorplans.length) {
    return null
  }
  let fp = null
  // filters info
  const filterBedrooms = filters ? filters.bedrooms : []
  // previously sent listing's bedroom info
  let messagedBedrooms = localStorageSingleton.getx('messagedBedrooms')
  let visitedBedrooms = localStorageSingleton.getx('visitedBedrooms')
  // first select floor plan using filter info
  if (!fp && filterBedrooms.length) {
    // If the user has a bedroom filter set, default to a floor plan that matches that bedroom count
    fp = floorplans.find((item) => filterBedrooms[item.bedrooms])
  }
  // select from previously messaged bedrooms history
  if (!fp && isNumber(messagedBedrooms)) {
    // If the user messaged a listing/building with a specific bedroom, we can assume the user
    // is interested in that configuration.
    messagedBedrooms = Number(messagedBedrooms)
    fp = floorplans.find((item) => messagedBedrooms === item.bedrooms)
  }
  // select from previously visited bedrooms history
  if (!fp && isNumber(visitedBedrooms)) {
    // If the user visited another listing page, look at that bedroom count and see if there
    // is a matching floor plan
    visitedBedrooms = Number(visitedBedrooms)
    fp = floorplans.find((item) => {
      return visitedBedrooms === item.bedrooms
    })
  }
  // no floor plan has been selected, choose either highest price or the first floor plan
  if (!fp) {
    ;[fp] = floorplans.slice().sort((a, b) => {
      const isRoomA = a.propertyType === PropertyType.ROOM
      const isRoomB = b.propertyType === PropertyType.ROOM
      if (isRoomA && isRoomB) {
        return 0
      } else if (isRoomA) {
        return 1
      } else if (isRoomB) {
        return -1
      }
      return (b.price ?? 0) - (a.price ?? 0)
    })
  }

  return fp
}

interface GetSimilarListingApiParams {
  entity: Property | Listable
  favorited: MaybeEmpty<BuildingListingCollection>
  filters: Filters
  forMultiMessage?: true
  limit: number
  messaged: BuildingListingCollection
  messageable: true
}

const getSimilarListingApiParams = ({
  entity,
  favorited,
  filters,
  forMultiMessage,
  limit,
  messaged,
  messageable,
}: GetSimilarListingApiParams) => {
  const negativeInt = (value: `${number}`) => {
    // eslint-disable-next-line
    const numericValue = ~~value
    return numericValue > 0 ? -numericValue : numericValue
  }
  // exclude messaged listings and buildings from this query
  const messagedBuildingIds: number[] = []
  const messagedListingIds: number[] = []

  oforEach(messaged.listings, (date, id) => {
    if (id) {
      messagedListingIds.push(negativeInt(id))
    }
  })
  oforEach(messaged.buildings, (date, id) => {
    if (id) {
      messagedBuildingIds.push(negativeInt(id))
    }
  })
  oforEach(favorited.listings, (date, id) => {
    if (id) {
      messagedListingIds.push(negativeInt(id))
    }
  })
  oforEach(favorited.buildings, (date, id) => {
    if (id) {
      messagedBuildingIds.push(negativeInt(id))
    }
  })

  // exclude this entity from the query
  if (entity.isBuilding) {
    messagedBuildingIds.push(-entity.buildingId)
  } else if (entity.listingId) {
    messagedListingIds.push(-entity.listingId)
  }

  const filterMaxPrice =
    filters.maxPrice !== MAX_FILTER_PRICE || filters.customMaxPrice ? filters.maxPrice : null

  const maxPrice = filterMaxPrice || entity.maxPrice || entity.price || undefined
  const minPrice = filters.minPrice || entity.minPrice || entity.price || undefined

  let bedroomsArray: Filters['bedrooms']
  const hasSetBedroomsFilter = filters.bedrooms?.some((el) => el)
  if (hasSetBedroomsFilter) {
    bedroomsArray = filters.bedrooms
  } else {
    const maxBeds = entity.maxBedrooms || entity.bedrooms || 0
    const minBeds = entity.minBedrooms || entity.bedrooms || 0
    bedroomsArray = [
      minBeds <= 0 && maxBeds >= 0,
      minBeds <= 1 && maxBeds >= 1,
      minBeds <= 2 && maxBeds >= 2,
      minBeds <= 3 && maxBeds >= 3,
      minBeds <= 4 && maxBeds >= 4,
      minBeds <= 5 && maxBeds >= 5,
    ]
  }

  const similarListingFilters = {
    ...getBox(entity),
    bedrooms: bedroomsArray,
    buildingIds: messagedBuildingIds,
    countries: entity.country ? [entity.country] : null,
    external: false,
    feeds: ['-hot_pads'],
    flavor: 1,
    floorplans: entity.isBuilding ? 1 : null,
    hasImages: true,
    limit: limit || 10,
    listingIds: messagedListingIds,
    maxPrice: maxPrice ? Math.round(maxPrice * 1.15) : undefined,
    messageable,
    minPrice: minPrice ? Math.round(minPrice * 0.85) : undefined,
    notFeatures: forMultiMessage ? [PropertyFeatures.MM_EXCLUDE] : undefined,
    offset: 0,
    longTerm: true,
    shortTerm: false,
    sort: ['-promotion:nl', 'random'],
  } as const

  return getAPIFriendlyFilters({ filters: similarListingFilters })
}

function getBox({ lat, lng }: Listable | Property) {
  if (lat == null || lng == null) {
    return
  }
  const { lat: latDelta, lng: lngDelta } = feetToDegreesAtLatitude(lat, [
    SIX_MILE_DIAG_BOX_SIDE_FEET,
  ])[0]
  return {
    maxLat: lat + latDelta,
    maxLng: lng + lngDelta,
    minLat: lat - latDelta,
    minLng: lng - lngDelta,
  }
}

interface LoadMoreSimilarListingsConfig {
  entity: Listable | Property
  favorited: MaybeEmpty<BuildingListingCollection>
  filters: Filters
  forMultiMessage?: true
  limit: number
  messaged: BuildingListingCollection
}

export const loadMoreSimilarListings = async (
  config: LoadMoreSimilarListingsConfig
): Promise<Listable[]> => {
  const { entity, favorited, filters, forMultiMessage, limit = 10, messaged } = config
  const unique = {
    buildings: new Set<number>(),
    listings: new Set<number>(),
  }
  if (!filters?.url && (!entity?.lat || !entity?.lng)) {
    const errorMessage =
      'Skipping listables api call, since either url or lat+lng are required, and neither was received.'
    logger.error(errorMessage)
    return []
  }

  const { floorplans, isActive, isBuilding } = entity

  const listingCheck = (listing: ListableListing) => {
    if (listing.listingId !== entity.listingId && !unique.listings.has(listing.listingId)) {
      unique.listings.add(listing.listingId)
      return true
    }
    return false
  }

  const buildingCheck = (listing: ListableBuilding) => {
    if (listing.buildingId !== entity.buildingId && !unique.buildings.has(listing.buildingId)) {
      unique.buildings.add(listing.buildingId)
      return true
    }
    return false
  }

  // newSimilarListings has to be JS array, not immutable
  const getUnique = (newSimilarListings: readonly Listable[]) => {
    return newSimilarListings.filter((similar) => {
      return similar.isBuilding ? buildingCheck(similar) : listingCheck(similar)
    })
  }

  const similarListingTarget = isBuilding ? selectFloorplan(filters, floorplans) : entity

  const query = getSimilarListingApiParams({
    entity,
    favorited,
    filters,
    forMultiMessage,
    limit,
    messaged,
    messageable: true,
  })

  if (!isActive || similarListingTarget?.listingId) {
    try {
      query.sort = ['mrrcap1']
      const { listables } = await Api.listables.query(query)
      return getUnique(listables)
    } catch (error: unknown) {
      logger.info('loadMoreSimilarListings failed:', error)
      throw error
    }
  }
  return []
}
